// Director-only AI-logic settings — wrappers around /api/miniapp/ai-settings.
// Lets the business tune the per-stage AI logic (what each stage means, the
// move triggers, the manager script) + the auto-execute switch that turns the
// AI's stage-move decisions from dry-run into live CRM moves.
import { apiRequest } from './client'

export interface AiStageContext {
  purpose?: string | null
  entry_conditions?: string | null
  exit_conditions?: string | null
}
export interface AiTransition {
  to_pipeline: string
  to_stage: string
  trigger: string
  priority: number
}
export interface AiScriptStep {
  n?: number
  title: string
  required: boolean
  description?: string | null
}
export interface AiScript {
  steps: AiScriptStep[]
}
export interface AiStageSettings {
  context: AiStageContext | null
  transitions: AiTransition[]
  script: AiScript | null
  stage_tasks: unknown[]
}
export interface AiSettingsStage {
  ext_id: string
  name: string
  settings: AiStageSettings
}
export interface AiSettingsPipeline {
  pipeline_ext_id: string
  name: string
  is_default: boolean
  stages: AiSettingsStage[]
}
export interface AiProfile {
  business?: string | null
  tone?: string | null
  reply_language?: string | null
  glossary?: string[]
  priorities?: string[]
  forbidden?: string[]
}
export interface AiSettingsResponse {
  auto_execute_moves: boolean
  ai_profile: AiProfile
  pipelines: AiSettingsPipeline[]
}

/** The editable payload for one stage (sections sent to the backend). */
export type StagePayload = Pick<
  AiStageSettings,
  'context' | 'transitions' | 'script' | 'stage_tasks'
>

export function fetchAiSettings(): Promise<AiSettingsResponse> {
  return apiRequest<AiSettingsResponse>('/api/miniapp/ai-settings')
}

export function saveStageLogic(
  pipelineExtId: string,
  stageExtId: string,
  payload: StagePayload,
): Promise<AiStageSettings> {
  return apiRequest<AiStageSettings>(
    `/api/miniapp/ai-settings/stage/${encodeURIComponent(pipelineExtId)}/${encodeURIComponent(stageExtId)}`,
    { method: 'PATCH', body: payload },
  )
}

export function setAutoExecute(enabled: boolean): Promise<{ auto_execute_moves: boolean }> {
  return apiRequest<{ auto_execute_moves: boolean }>(
    '/api/miniapp/ai-settings/auto-execute',
    { method: 'PATCH', body: { enabled } },
  )
}

export function saveAiProfile(profile: AiProfile): Promise<{ ai_profile: AiProfile }> {
  return apiRequest<{ ai_profile: AiProfile }>(
    '/api/miniapp/ai-settings/profile',
    { method: 'PATCH', body: profile },
  )
}
